<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  dashboard: {
    type: Object,
    default: () => ({ pending: [], pending_charges: [] }),
  },
  loading: { type: Boolean, default: false },
});

const emit = defineEmits(['open', 'openPendingCharge']);
const { t } = useI18n();
const rows = computed(() => {
  const charges = (props.dashboard.pending_charges || []).map(item => ({
    item,
    type: 'pending_charge',
  }));
  const sales = (props.dashboard.pending || []).map(item => ({
    item,
    type: 'sale',
  }));

  return [...charges, ...sales];
});
const title = item => item.title || item.reference || t('SALES.COMMON.NO_DATA');
const address = item =>
  item.address?.endereco || t('SALES.COMMON.ADDRESS_UNAVAILABLE');
const reason = item =>
  item.review?.reason || item.sales_stage_label || t('SALES.STATUS.PENDING');
const chargeAddress = item => {
  const firstLine = [item.logradouro, item.numero].filter(Boolean).join(', ');
  const locality = [item.bairro, item.cidade].filter(Boolean).join(', ');
  const city = item.uf ? `${locality} - ${item.uf}` : locality;

  return (
    [firstLine, item.unidade, item.complemento, city]
      .filter(Boolean)
      .join(', ') || t('SALES.COMMON.ADDRESS_UNAVAILABLE')
  );
};
const rowTitle = row =>
  row.type === 'pending_charge'
    ? t('SALES.PENDING_CHARGE.STATUS')
    : title(row.item);
const rowAddress = row =>
  row.type === 'pending_charge' ? chargeAddress(row.item) : address(row.item);
const rowStatus = row => {
  if (row.type === 'pending_charge') {
    return t('SALES.OVERVIEW.INSTALLMENT', {
      current: row.item.numero_parcela,
      total: row.item.total_parcelas,
    });
  }

  return reason(row.item);
};
const openRow = row => {
  if (row.type === 'pending_charge') {
    emit('openPendingCharge', row.item);
    return;
  }

  emit('open', row.item);
};
</script>

<template>
  <section
    class="min-w-0 rounded-lais border border-lais-border-soft bg-white p-4 shadow-lais-card sm:p-5"
  >
    <div class="flex items-center gap-2">
      <div class="flex items-center gap-2">
        <span
          class="i-lucide-list-checks size-5 text-lais-primary"
          aria-hidden="true"
        />
        <h2 class="font-redHatDisplay text-lg font-semibold text-lais-text">
          {{ t('SALES.OVERVIEW.PENDING') }}
        </h2>
      </div>
      <span
        class="rounded-full bg-lais-primary-50 px-2 py-0.5 text-xs font-bold text-lais-primary"
      >
        {{ rows.length }}
      </span>
      <label
        class="ml-auto flex items-center gap-2 text-xs text-lais-text-muted"
      >
        <span>{{ t('SALES.OVERVIEW.FILTER_LABEL') }}</span>
        <select
          class="rounded-lais border border-lais-border-soft bg-white px-2 py-1 text-xs text-lais-text"
          :aria-label="t('SALES.OVERVIEW.FILTER_LABEL')"
          value="today"
        >
          <option value="today">{{ t('SALES.OVERVIEW.TODAY') }}</option>
        </select>
      </label>
    </div>

    <div
      v-if="loading"
      class="mt-4 h-32 animate-pulse rounded-lais bg-lais-bg-soft"
    />
    <div
      v-else-if="!rows.length"
      class="mt-4 rounded-lais border border-dashed border-lais-border-soft p-6 text-center text-sm text-lais-text-muted"
    >
      {{ t('SALES.OVERVIEW.EMPTY_PENDING') }}
    </div>
    <div v-else class="mt-3 divide-y divide-lais-border-soft">
      <button
        v-for="item in rows"
        :key="`${item.type}-${item.item.processo_id || item.item.id || item.item.parcela_id}`"
        data-testid="sales-overview-pending-row"
        type="button"
        class="flex w-full items-center justify-between gap-3 py-3 text-left first:pt-0 last:pb-0"
        @click="openRow(item)"
      >
        <span class="min-w-0">
          <span class="block truncate text-sm font-semibold text-lais-text">
            {{ rowTitle(item) }}
          </span>
          <span class="mt-1 block truncate text-xs text-lais-text-muted">
            {{ rowAddress(item) }}
          </span>
        </span>
        <span class="shrink-0 text-right">
          <span class="block text-xs font-semibold text-orange-600">
            {{ rowStatus(item) }}
          </span>
          <span
            class="mt-1 flex items-center justify-end gap-2 text-xs text-lais-primary"
          >
            <span
              v-if="item.item.status_visualizacao === 0"
              class="rounded-full bg-lais-success/10 px-2 py-0.5 font-bold text-lais-success"
            >
              {{ t('SALES.OVERVIEW.NEW') }}
            </span>
            <span>
              {{
                item.type === 'pending_charge'
                  ? t('SALES.OVERVIEW.REVIEW_DATA')
                  : t('SALES.ACTIONS.DETAILS')
              }}
            </span>
          </span>
        </span>
      </button>
    </div>
  </section>
</template>
